import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import Navbar from "../components/Navbar";
import HouseRulesModal from "../components/HouseRulesModal";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // When the signup page is reached from a role-specific CTA (e.g. "Join as
  // Staff" or "Create employer account"), lock the form to that role and hide
  // the tab switcher so the user sees only their flow.
  const lockedRole = useMemo(() => {
    const r = params.get("role");
    return r === "employee" || r === "customer" ? r : null;
  }, [params]);
  const [role, setRole] = useState(lockedRole || "customer");

  // Keep the role state in sync with the URL ?role= param so that clicking the
  // in-page switch link ("I'm staff instead" / "Looking to hire instead?")
  // flips the form immediately without needing a hard refresh.
  useEffect(() => {
    if (lockedRole && lockedRole !== role) {
      setRole(lockedRole);
      setAgreedVersion(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedRole]);

  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [agreedVersion, setAgreedVersion] = useState(null);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone: "254",
    category: "",
    bio: "",
    skills: "",
    experience_years: 0,
    location: "Nairobi",
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^254[17]\d{8}$/.test(form.phone)) {
      toast.error("Phone must be in format 254XXXXXXXXX");
      return;
    }
    if (role === "employee" && !agreedVersion) {
      toast.error("You must read and agree to the KSB House Rules first");
      setRulesOpen(true);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        role,
      };
      if (role === "employee") {
        if (!form.category) {
          toast.error("Choose a category");
          setBusy(false);
          return;
        }
        Object.assign(payload, {
          category: form.category,
          bio: form.bio,
          skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
          experience_years: Number(form.experience_years),
          location: form.location,
          agreed_to_house_rules: true,
          house_rules_version: agreedVersion,
        });
      }
      const u = await signup(payload);
      // Flag a fresh signup so the InstallNudge shows immediately after
      // landing on the new user's dashboard. Auto-hides if the app is
      // already installed (standalone display-mode).
      localStorage.setItem("ksb_new_signup", "1");
      toast.success(`Welcome to KSB, ${u.full_name.split(" ")[0]}`);
      const next = params.get("next");
      if (next) nav(next);
      else nav(role === "employee" ? "/employee" : "/customer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-xl mx-auto px-6 py-14">
        <div className="label-eyebrow mb-3">Join KSB</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">
          {lockedRole === "employee" ? "Join as staff" : lockedRole === "customer" ? "Create your employer account" : "Create your account"}
        </h1>
        {lockedRole && (
          <p className="text-[#5B6478] mt-2 text-sm">
            {lockedRole === "employee"
              ? "Set up your staff profile — employers browse KSB daily."
              : "Set up your employer account to request a match."}
            {" "}
            <Link
              to={`/signup?role=${lockedRole === "employee" ? "customer" : "employee"}`}
              data-testid="signup-switch-role"
              className="text-[#1E3A8A] font-semibold hover:underline"
            >
              {lockedRole === "employee" ? "Looking to hire instead?" : "I'm staff instead"}
            </Link>
          </p>
        )}

        <Tabs value={role} onValueChange={(v) => { setRole(v); setAgreedVersion(null); }} className="mt-8">
          {!lockedRole && (
            <TabsList className="bg-[#EEF0FA] grid grid-cols-2 w-full h-12">
              <TabsTrigger value="customer" data-testid="signup-tab-customer" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-[#F8F9FE]">
                I need staff
              </TabsTrigger>
              <TabsTrigger value="employee" data-testid="signup-tab-employee" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-[#F8F9FE]">
                I'm staff
              </TabsTrigger>
            </TabsList>
          )}

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label className="label-eyebrow">Full name</Label>
                <Input data-testid="signup-fullname" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">Email</Label>
                  <Input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" />
                </div>
                <div>
                  <Label className="label-eyebrow">Phone (M-Pesa)</Label>
                  <Input data-testid="signup-phone" required placeholder="254712345678" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Password</Label>
                <Input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" />
              </div>

              <TabsContent value="employee" className="space-y-5 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="label-eyebrow">Category</Label>
                    <Select value={form.category} onValueChange={(v) => update("category", v)}>
                      <SelectTrigger data-testid="signup-category" className="mt-2 h-12 border-[#E4E7F5]">
                        <SelectValue placeholder="Select your work" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-eyebrow">Years experience</Label>
                    <Input data-testid="signup-experience" type="number" min="0" value={form.experience_years} onChange={(e) => update("experience_years", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" />
                  </div>
                </div>
                <div>
                  <Label className="label-eyebrow">Short bio</Label>
                  <Textarea data-testid="signup-bio" rows={3} value={form.bio} onChange={(e) => update("bio", e.target.value)} className="mt-2 border-[#E4E7F5]" placeholder="Tell employers about yourself" />
                </div>
                <div>
                  <Label className="label-eyebrow">Skills (comma separated)</Label>
                  <Input data-testid="signup-skills" value={form.skills} onChange={(e) => update("skills", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" placeholder="Cleaning, Cooking, Laundry" />
                </div>
                <div>
                  <Label className="label-eyebrow">Location</Label>
                  <Input data-testid="signup-location" value={form.location} onChange={(e) => update("location", e.target.value)} className="mt-2 h-12 border-[#E4E7F5]" />
                </div>
                <div className="bg-[#EEF0FA] border border-[#E4E7F5] rounded-md p-4 flex items-start gap-3">
                  <ShieldCheck className={`w-5 h-5 mt-0.5 shrink-0 ${agreedVersion ? "text-[#1E3A8A]" : "text-[#7C3AED]"}`} />
                  <div className="flex-1">
                    <div className="font-display font-semibold text-sm">
                      {agreedVersion ? `House Rules agreed (${agreedVersion})` : "You must agree to the KSB House Rules"}
                    </div>
                    <p className="text-xs text-[#5B6478] mt-1">
                      Every staff member must read and accept our code of conduct before being listed. Rate-setting is handled by the bureau.
                    </p>
                    <Button
                      type="button"
                      data-testid="open-house-rules"
                      variant="outline"
                      size="sm"
                      className="mt-3 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-[#F8F9FE]"
                      onClick={() => setRulesOpen(true)}
                    >
                      {agreedVersion ? "Review Rules" : "Read & Agree"}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </div>

            <Button data-testid="signup-submit" type="submit" disabled={busy} className="w-full h-12 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">
              {busy ? "Creating account..." : "Create account"}
            </Button>
          </form>
        </Tabs>

        <div className="text-sm text-[#5B6478] mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-[#1E3A8A] hover:underline">Sign in</Link>
        </div>
      </div>
      <HouseRulesModal open={rulesOpen} onOpenChange={setRulesOpen} onAgree={(v) => { setAgreedVersion(v); toast.success("House Rules acknowledged"); }} />
    </div>
  );
}
